const u = (id) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=1200&q=70`;

/* Guided-tour narration — one spoken-style card per site. */
export const NARRATION = {
  "giza-pyramid":
    "We begin where every argument begins. Two and a half million blocks, aligned to true north closer than most modern buildings. Look at the three shadows on the plateau — then look up at Orion's Belt.",
  "great-sphinx":
    "A few hundred metres south, the lion with the small head. Run your hand down the enclosure wall and you feel vertical channels — rain erosion, say the geologists. Egypt hasn't had that rain for nine thousand years.",
  "stonehenge":
    "Across the Channel now, to a circle on a chalk plain. Strike the bluestones and they ring like bells. They were carried here from Wales. Nobody carries a bell one hundred and eighty miles by accident.",
  "gobekli-tepe":
    "Anatolia, eleven and a half thousand years ago. Hunter-gatherers raised T-shaped pillars weighing ten tonnes — then, two millennia later, buried the whole sanctuary on purpose. Nothing about that is comfortable.",
  "gunung-padang":
    "A green hill in West Java. Beneath the visible terraces, cores came back with dates that shouldn't exist. The paper was retracted. The hill is still there, humming when struck.",
  "easter-island":
    "The loneliest inhabited place on Earth. The heads have bodies — buried to the chest — and the islanders say they walked here. In 2012 someone tried it with ropes. They walked.",
  "nazca-lines":
    "Over the Peruvian desert, lines that only make sense from the sky. A hummingbird, a monkey, a spider. Below them, water moves through the rock — and the lines seem to know where.",
  "machu-picchu":
    "Climb into the cloud forest. Two kinds of stone work sit on one ridge: Inca above, and beneath it, joints so tight a blade won't pass. The question is who cut the lower course.",
  "puma-punku":
    "Three thousand eight hundred metres up on the Altiplano. H-blocks with interior right angles, repeated like a casting mould. The whole site lies scattered — as if something hit it.",
  "baalbek":
    "The Beqaa Valley. Three stones in the podium weigh eight hundred tonnes each, and a fourth still sleeps in the quarry at over a thousand. Rome built on top. The question is what Rome built on.",
  "mohenjo-daro":
    "The Indus. A planned city with sewers and standard bricks, emptied without a palace, a temple, or an explanation. Skeletons were found in the street. The script has never been read.",
  "yonaguni":
    "Dive now, off the last island of Japan. Terraces with right angles, a 'road', a wedge. Wave-cut sandstone, insists one camp. A drowned monument, insists the other. The water doesn't care.",
  "teotihuacan":
    "Mexico. The city so old the Aztecs found it empty and named it the birthplace of the gods. Mica from Brazil lines hidden chambers; liquid mercury pools beneath the Feathered Serpent.",
  "nan-madol":
    "A reef in Micronesia, and on it, a city of basalt logs — three quarters of a million tonnes. Legend says two brothers flew the stones here. Engineers have no better story yet.",
  "derinkuyu":
    "We end underground, in Cappadocia. Eighteen levels, eighty-five metres down, air still moving to the bottom without a single machine. Someone expected to stay a long time.",
};

const SPHINX = u("photo-1780568507851-8d522f0570da");
const STONES = u("photo-1595878263670-8db8723ce4fa");
const INCA_WALL = u("photo-1642193013675-aecbe08bd30b");
const BAALBEK_UP = u("photo-1587670446057-95446ca3f1a7");
const BAALBEK_COL = u("photo-1547126627-864d9d39c259");
const NAZCA_BIRD = u("photo-1602839993317-7b77dff450d0");
const NAZCA_TREE = u("photo-1602839993248-0e2b8b6e4bd1");
const CHAMBER = u("photo-1590669911198-42b3ac24a3d1");

/* Anomaly plates — per site, one per hypothesis (index matches site.hypotheses).
   x/y: crop focus (%), zoom: close-up scale, pins: annotated callouts (%). */
export const PLATES = {
  "giza-pyramid": [
    {
      x: 50, y: 30, zoom: 1.6,
      pins: [
        { x: 30, y: 55, label: "Khufu — Alnitak" },
        { x: 52, y: 48, label: "Khafre — Alnilam" },
        { x: 76, y: 40, label: "Menkaure — Mintaka, offset like the star" },
      ],
    },
    {
      image: CHAMBER, x: 50, y: 50, zoom: 1.2,
      pins: [
        { x: 50, y: 60, label: "Granite walls — a piezoelectric mineral" },
        { x: 20, y: 30, label: "Shafts angled, never used for air" },
        { x: 78, y: 70, label: "Empty, lidless 'sarcophagus'" },
      ],
    },
    {
      x: 50, y: 60, zoom: 1.9,
      pins: [
        { x: 50, y: 25, label: "Apex — true north within 3 arc-minutes" },
        { x: 32, y: 65, label: "Concave face, visible at equinox" },
        { x: 70, y: 80, label: "No royal inscription anywhere" },
      ],
    },
  ],
  "great-sphinx": [
    {
      image: SPHINX, x: 40, y: 70, zoom: 1.5,
      pins: [
        { x: 30, y: 72, label: "Vertical fissures — rain-run erosion" },
        { x: 60, y: 85, label: "Rounded, undulating profile of the body" },
        { x: 85, y: 55, label: "Harder limestone of the head, less weathered" },
      ],
    },
    {
      image: SPHINX, x: 60, y: 30, zoom: 1.4,
      pins: [
        { x: 55, y: 30, label: "Head too small for the body — recarved?" },
        { x: 70, y: 48, label: "Nemes headdress cut back into older stone" },
        { x: 25, y: 70, label: "Body mass implies a far larger original head" },
      ],
    },
    {
      image: SPHINX, x: 30, y: 85, zoom: 1.7,
      pins: [
        { x: 35, y: 85, label: "Forepaws — seismic voids reported beneath" },
        { x: 65, y: 90, label: "Bedrock floor, never fully surveyed" },
        { x: 15, y: 60, label: "Enclosure wall channels" },
      ],
    },
  ],
  "stonehenge": [
    {
      image: STONES, x: 50, y: 60, zoom: 1.5,
      pins: [
        { x: 25, y: 60, label: "Bluestone — rings when struck" },
        { x: 55, y: 45, label: "Sarsen lintel, tenon-jointed" },
        { x: 80, y: 70, label: "Acoustic reflection inside the ring" },
      ],
    },
    {
      image: STONES, x: 30, y: 65, zoom: 1.7,
      pins: [
        { x: 30, y: 65, label: "Preseli dolerite, 180 miles from source" },
        { x: 65, y: 55, label: "Chips found in burials — 'healing' amulets" },
      ],
    },
    {
      x: 50, y: 50, zoom: 1.3,
      pins: [
        { x: 50, y: 50, label: "Centre — solstice axis" },
        { x: 15, y: 50, label: "Great-circle to Carnac" },
        { x: 85, y: 55, label: "Great-circle to Giza" },
      ],
    },
  ],
  "gobekli-tepe": [
    {
      x: 50, y: 45, zoom: 1.6,
      pins: [
        { x: 45, y: 40, label: "Pillar 43 — the 'Vulture Stone'" },
        { x: 65, y: 30, label: "Disc read as the Sun at impact" },
        { x: 30, y: 70, label: "Scorpion — Scorpius?" },
      ],
    },
    {
      x: 50, y: 70, zoom: 1.5,
      pins: [
        { x: 50, y: 70, label: "Deliberate backfill — rubble, bones, flint" },
        { x: 20, y: 40, label: "Enclosure buried while intact" },
      ],
    },
    {
      x: 50, y: 40, zoom: 1.4,
      pins: [
        { x: 40, y: 45, label: "Arms carved on the pillar — anthropomorphic" },
        { x: 70, y: 60, label: "No domestic hearths, no fields" },
        { x: 20, y: 25, label: "Older than pottery" },
      ],
    },
  ],
  "gunung-padang": [
    {
      x: 50, y: 65, zoom: 1.6,
      pins: [
        { x: 50, y: 75, label: "Layer 3 — contested 27,000-year dates" },
        { x: 30, y: 45, label: "Layer 1 terraces, c. 2000 BCE" },
      ],
    },
    {
      x: 50, y: 50, zoom: 1.4,
      pins: [
        { x: 50, y: 50, label: "Sunda shelf — dry land in the Ice Age" },
        { x: 75, y: 30, label: "Hilltop sanctuary above a drowned plain" },
      ],
    },
    {
      x: 40, y: 60, zoom: 1.8,
      pins: [
        { x: 40, y: 60, label: "Andesite column — rings at 2 tones" },
        { x: 70, y: 70, label: "'Singing stones' of local lore" },
      ],
    },
  ],
  "easter-island": [
    {
      x: 50, y: 55, zoom: 1.5,
      pins: [
        { x: 50, y: 85, label: "D-shaped base — rocks forward when tilted" },
        { x: 50, y: 30, label: "Forward-leaning posture while 'walking'" },
        { x: 20, y: 60, label: "Rope-wear on the neck" },
      ],
    },
    {
      x: 50, y: 50, zoom: 1.3,
      pins: [
        { x: 30, y: 75, label: "Rock-mulch gardens — not collapse farming" },
        { x: 70, y: 40, label: "Population stable to contact (sediment cores)" },
      ],
    },
    {
      x: 50, y: 45, zoom: 1.7,
      pins: [
        { x: 50, y: 45, label: "Rongorongo — reverse boustrophedon script" },
        { x: 25, y: 25, label: "Glyphs resemble no Polynesian system" },
      ],
    },
  ],
  "nazca-lines": [
    {
      image: NAZCA_BIRD, x: 50, y: 55, zoom: 1.4,
      pins: [
        { x: 50, y: 55, label: "Hummingbird, 93 m — legible only from above" },
        { x: 15, y: 30, label: "Straight line crossing broken ground" },
      ],
    },
    {
      image: NAZCA_TREE, x: 55, y: 55, zoom: 1.5,
      pins: [
        { x: 55, y: 60, label: "Glyph follows a buried fault — aquifer trace" },
        { x: 30, y: 30, label: "Puquios (spiral wells) nearby" },
      ],
    },
    {
      image: NAZCA_BIRD, x: 50, y: 55, zoom: 1.8,
      pins: [
        { x: 50, y: 60, label: "Single unbroken line — a walkable path" },
        { x: 75, y: 40, label: "Entry and exit at the same point" },
      ],
    },
  ],
  "machu-picchu": [
    {
      image: INCA_WALL, x: 50, y: 55, zoom: 1.6,
      pins: [
        { x: 35, y: 55, label: "Polygonal course — no mortar, knife-tight" },
        { x: 70, y: 30, label: "Coarser Inca field-stone above" },
        { x: 20, y: 80, label: "Blocks of 100+ tons" },
      ],
    },
    {
      x: 50, y: 40, zoom: 1.6,
      pins: [
        { x: 50, y: 40, label: "Intihuatana — 'hitching post of the Sun'" },
        { x: 75, y: 60, label: "Window frames the June solstice sunrise" },
      ],
    },
    {
      x: 50, y: 50, zoom: 1.3,
      pins: [
        { x: 45, y: 50, label: "Radiocarbon samples pushed to c. 1420 CE" },
        { x: 70, y: 70, label: "Occupation earlier than Pachacuti's reign" },
      ],
    },
  ],
  "puma-punku": [
    {
      x: 50, y: 55, zoom: 1.8,
      pins: [
        { x: 50, y: 55, label: "H-block — identical interior right angles" },
        { x: 25, y: 40, label: "Blind drill-holes along a straight groove" },
        { x: 75, y: 70, label: "Surfaces flat to within a millimetre" },
      ],
    },
    {
      x: 50, y: 35, zoom: 1.4,
      pins: [
        { x: 50, y: 35, label: "Kalasasaya corner — solstice sightline" },
        { x: 20, y: 60, label: "Obliquity match argued for 15,000 BCE" },
      ],
    },
    {
      x: 50, y: 65, zoom: 1.5,
      pins: [
        { x: 50, y: 65, label: "Blocks tumbled, not quarried in place" },
        { x: 25, y: 45, label: "Lake sediment layered over the ruins" },
      ],
    },
  ],
  "baalbek": [
    {
      image: BAALBEK_UP, x: 50, y: 80, zoom: 1.6,
      pins: [
        { x: 50, y: 85, label: "Trilithon course — 800 t per stone" },
        { x: 25, y: 60, label: "Joints too tight for a blade" },
        { x: 80, y: 45, label: "Roman podium stones, far smaller" },
      ],
    },
    {
      image: BAALBEK_COL, x: 50, y: 85, zoom: 1.5,
      pins: [
        { x: 50, y: 88, label: "Pre-Roman platform beneath the temple" },
        { x: 20, y: 50, label: "Change in tooling marks at the seam" },
      ],
    },
    {
      x: 50, y: 60, zoom: 1.4,
      pins: [
        { x: 50, y: 60, label: "Hajjar al-Hibla — 1,000 t, still in quarry" },
        { x: 80, y: 80, label: "A third, larger stone found beneath it (2014)" },
      ],
    },
  ],
  "mohenjo-daro": [
    {
      x: 50, y: 60, zoom: 1.7,
      pins: [
        { x: 50, y: 60, label: "Glassy fused brick — kiln slag or something hotter?" },
        { x: 25, y: 35, label: "Standard 4:2:1 brick module" },
      ],
    },
    {
      x: 50, y: 45, zoom: 1.4,
      pins: [
        { x: 50, y: 45, label: "Citadel mound — 'the mound of the dead'" },
        { x: 75, y: 70, label: "Skeletons in the lanes, unburied" },
      ],
    },
    {
      x: 50, y: 55, zoom: 1.5,
      pins: [
        { x: 50, y: 55, label: "Covered drains running under every street" },
        { x: 25, y: 70, label: "No palace, no temple identified" },
        { x: 80, y: 30, label: "Seal script — 400 signs, unread" },
      ],
    },
  ],
  "yonaguni": [
    {
      x: 50, y: 50, zoom: 1.6,
      pins: [
        { x: 50, y: 50, label: "'Main terrace' — flat top, right-angled drop" },
        { x: 25, y: 70, label: "Kimura's 'loop road' around the base" },
        { x: 75, y: 30, label: "'Turtle' carving" },
      ],
    },
    {
      x: 50, y: 65, zoom: 1.4,
      pins: [
        { x: 50, y: 65, label: "25 m depth — above sea level c. 10,000 BCE" },
        { x: 20, y: 40, label: "Holocene sea-rise line" },
      ],
    },
    {
      x: 50, y: 50, zoom: 1.9,
      pins: [
        { x: 50, y: 50, label: "Bedding planes — sandstone splits flat" },
        { x: 75, y: 70, label: "Joint sets at 90° occur naturally" },
      ],
    },
  ],
  "teotihuacan": [
    {
      x: 50, y: 55, zoom: 1.3,
      pins: [
        { x: 50, y: 55, label: "Avenue of the Dead — Harleston's 'orbit' scale" },
        { x: 20, y: 40, label: "Pyramid of the Moon = Uranus distance?" },
        { x: 80, y: 70, label: "Citadel = Sun" },
      ],
    },
    {
      x: 50, y: 70, zoom: 1.7,
      pins: [
        { x: 50, y: 70, label: "Mica sheet layer — Brazilian source" },
        { x: 25, y: 45, label: "Sealed beneath a floor, never visible" },
      ],
    },
    {
      x: 50, y: 60, zoom: 1.5,
      pins: [
        { x: 50, y: 60, label: "Tunnel entrance under the Feathered Serpent" },
        { x: 75, y: 80, label: "Liquid mercury pooled in chambers" },
        { x: 20, y: 30, label: "Pyrite 'stars' on the ceiling" },
      ],
    },
  ],
  "nan-madol": [
    {
      x: 50, y: 50, zoom: 1.6,
      pins: [
        { x: 50, y: 50, label: "Prismatic basalt — up to 50 t per 'log'" },
        { x: 25, y: 30, label: "Sokehs quarry, 25 km across the island" },
      ],
    },
    {
      x: 50, y: 60, zoom: 1.3,
      pins: [
        { x: 50, y: 60, label: "Islet seawall — canal grid between" },
        { x: 80, y: 40, label: "92 artificial islets on the reef" },
      ],
    },
    {
      x: 50, y: 80, zoom: 1.6,
      pins: [
        { x: 50, y: 80, label: "Outer wall — reported pillars beyond it" },
        { x: 20, y: 60, label: "Divers' 'columns' at 20–30 m, unverified" },
      ],
    },
  ],
  "derinkuyu": [
    {
      x: 50, y: 50, zoom: 1.5,
      pins: [
        { x: 50, y: 50, label: "Level 8 of 18 — 85 m total depth" },
        { x: 25, y: 70, label: "Stables, chapels, wine presses" },
        { x: 75, y: 30, label: "Room for ~20,000" },
      ],
    },
    {
      x: 50, y: 65, zoom: 1.7,
      pins: [
        { x: 50, y: 65, label: "Lowest cuts — earliest, undated" },
        { x: 25, y: 40, label: "Byzantine-era enlargement above" },
      ],
    },
    {
      x: 50, y: 40, zoom: 1.6,
      pins: [
        { x: 50, y: 40, label: "Vertical shaft — 55 m, passive airflow" },
        { x: 75, y: 70, label: "Rolling stone door, closes from inside" },
      ],
    },
  ],
};
